/**
 * GenerationDataGrid — таблица результатов генерации.
 *
 * P1.3 (2026-08-14): мигрирована на единый `<DataTable>` — каркас
 * (pagination/pin/sort/filter/auto-scroll) делегирован; здесь только
 * динамические column-def'ы (по ключам generated_content) + cell-рендеры +
 * открытие detail drawer.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnDef } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { usePipelineRows, type PipelineRow } from '@/hooks/useGeneration';
import { useGenerationStore } from '@/store/generationStore';
import { DetailViewModal } from './DetailViewModal';
import { DataTable } from '@/components/shared/DataTable';
import { cn } from '@/lib/utils';

function firstGcValue(gc: unknown, key: string): unknown {
  if (!gc || typeof gc !== 'object') return undefined;
  const first = Object.values(gc as Record<string, Record<string, unknown>>)[0];
  return first?.[key];
}

export function GenerationDataGrid() {
  const { t } = useTranslation();
  const currentPipelineId = useGenerationStore((s) => s.currentPipelineId);
  const [selectedRow, setSelectedRow] = useState<PipelineRow | null>(null);

  const { data, isLoading } = usePipelineRows(currentPipelineId, 200);
  const rows = data?.rows ?? [];

  // Собираем колонки: row_index + status + динамические по generated_content keys
  const columns = useMemo<ColumnDef<PipelineRow>[]>(() => {
    const base: ColumnDef<PipelineRow>[] = [
      {
        accessorKey: 'row_index',
        header: '#',
        cell: (info) => <span className="text-surface-500">#{info.getValue<number>()}</span>,
        size: 60,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          const s = info.getValue<string>();
          return (
            <span
              className={cn(
                'text-xs px-1.5 py-0.5 rounded',
                s === 'success' && 'bg-success-tint/15 text-success',
                s === 'error' && 'bg-danger-tint/15 text-danger',
                s === 'processing' && 'bg-primary-500/15 text-primary-300',
                s === 'pending' && 'bg-surface-700/40 text-surface-400',
              )}
            >
              {s}
            </span>
          );
        },
        size: 90,
      },
    ];

    // Собираем все ключи generated_content (первого успешного row)
    const sample = rows.find((r) => r.generated_content)?.generated_content ?? {};
    const samplePayload = Object.values(sample)[0] as Record<string, unknown> | undefined;
    if (samplePayload && typeof samplePayload === 'object') {
      for (const key of Object.keys(samplePayload)) {
        base.push({
          id: `gc_${key}`,
          accessorFn: (row) => {
            const val = firstGcValue(row.generated_content, key);
            return Array.isArray(val) ? val.join(' · ') : val;
          },
          header: key,
          cell: (info) => {
            const val = firstGcValue(info.row.original.generated_content, key);
            if (Array.isArray(val)) {
              return (
                <div className="space-y-0.5">
                  {val.slice(0, 3).map((v, i) => (
                    <div key={i} className="text-xs">
                      • {String(v).slice(0, 80)}
                    </div>
                  ))}
                </div>
              );
            }
            const s = String(val ?? '—');
            return (
              <div className="text-sm max-w-md truncate" title={s}>
                {s.length > 120 ? s.slice(0, 120) + '…' : s}
              </div>
            );
          },
          enableColumnFilter: false,
        });
      }
    }
    return base;
  }, [rows]);

  if (!currentPipelineId) {
    return (
      <div className="card text-sm text-surface-500">
        {t('generation.results.empty', { defaultValue: 'Start a pipeline to see results.' })}
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="card flex items-center gap-2 text-sm text-surface-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('files.results.loading', { defaultValue: 'Reading results…' })}
      </div>
    );
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        pinLeftCount={1}
        onRowClick={(row) => setSelectedRow(row)}
        emptyState={t('generation.results.noRows', { defaultValue: 'No rows yet.' })}
      />
      {selectedRow && (
        <DetailViewModal row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </>
  );
}